import { Dictionary } from 'lodash';

import {
	DataPersonTct,
	DataSecurityTct,
	Holder,
	HoldingTotals,
	IssuerHolding,
	MipGroupHoldingResponse,
	SecurityHolding,
	SecurityIssuedIn,
	SecurityType,
	SecurityTypeHolding,
} from '@dealstack/shared/lib/generated';
import { getPercentage } from '@dealstack/shared/lib/format/number';

export const SECURITY_NOMINAL_VALUE_PRECISION = 5;

const SECURITY_TYPES_ORDER = [
	SecurityType.ORDINARY_SHARE,
	SecurityType.PREFERENCE_SHARE,
	SecurityType.TRACKING_SHARE,
	SecurityType.LOAN,
	SecurityType.WARRANT,
	SecurityType.PARTNERSHIP_UNIT,
	SecurityType.OPTIONS,
	SecurityType.VIRTUAL_EQUITY,
	SecurityType.OTHER,
	SecurityType.OTHER_EQUITY,
];

export const sortSecuritiesTypes = (
	SecurityTypeHoldings: IssuerHolding['securityTypesHoldings']
) => {
	const sortedSecurityTypeHoldings: SecurityTypeHolding[] = [];
	for (const securityType of SECURITY_TYPES_ORDER) {
		if (securityType in SecurityTypeHoldings) {
			sortedSecurityTypeHoldings.push(
				SecurityTypeHoldings[securityType] as SecurityTypeHolding
			);
		}
	}
	return sortedSecurityTypeHoldings;
};

export const sortSecuritiesAlphabetically = (securitiesHoldings: Dictionary<SecurityHolding>) => {
	const securities = Object.values(securitiesHoldings);
	return securities.sort((a, b) => a.securityName.localeCompare(b.securityName));
};

export const getEntityName = (
	entityData?: Pick<DataPersonTct, 'displayName' | 'firstName' | 'lastName'>
): string => {
	if (!entityData) {
		return '';
	}

	return entityData.displayName ?? `${entityData.firstName} ${entityData.lastName}`;
};

export const isSecurityIssuedInUnits = (issuedIn?: SecurityIssuedIn) =>
	issuedIn === SecurityIssuedIn.UNITS;

export const isSecurityIssuedInCurrency = (issuedIn?: SecurityIssuedIn) =>
	issuedIn === SecurityIssuedIn.CURRENCY;

/**
 * A management loan: a LOAN drawn against a programme's loan definition, issued by the borrower
 * (often an investment vehicle) rather than by the deal entity. An ordinary LOAN carries no
 * definition.
 */
export const isManagementLoanSecurity = (
	security?: Pick<DataSecurityTct, 'type' | 'loanDefinitionId'>
) => security?.type === SecurityType.LOAN && !!security.loanDefinitionId;

export const subtractSecurityFromSummarizedHolders = (
	summarizedHolders: Dictionary<Holder>,
	securityHolders: Dictionary<Holder>
): void => {
	for (const holderId in securityHolders) {
		const holderValuesToSubtract = securityHolders[holderId]!;
		const summarizedHolder = summarizedHolders[holderId]!;

		summarizedHolder.strict.amount -= holderValuesToSubtract.strict.amount;
		summarizedHolder.nonStrict.amount -= summarizedHolder.nonStrict.amount;

		if (
			holderValuesToSubtract.strict.totalCost !== null &&
			summarizedHolder.strict.totalCost !== null
		) {
			summarizedHolder.strict.totalCost -= holderValuesToSubtract.strict.totalCost;
		}

		if (
			holderValuesToSubtract.nonStrict.totalCost !== null &&
			summarizedHolder.nonStrict.totalCost !== null
		) {
			summarizedHolder.nonStrict.totalCost -= holderValuesToSubtract.nonStrict.totalCost;
		}

		if (holderValuesToSubtract.strict.votes && summarizedHolder.strict.votes) {
			summarizedHolder.strict.votes -= holderValuesToSubtract.strict.votes;
		}
		if (holderValuesToSubtract.nonStrict.votes && summarizedHolder.nonStrict.votes) {
			summarizedHolder.nonStrict.votes -= holderValuesToSubtract.nonStrict.votes;
		}
	}
};

export const subtractSecurityFromHoldingTotals = (
	holdingTotals: HoldingTotals,
	securityTotals: HoldingTotals
): void => {
	holdingTotals.strict.totalAmount -= securityTotals.strict.totalAmount;
	holdingTotals.nonStrict.totalAmount -= securityTotals.nonStrict.totalAmount;

	if (holdingTotals.strict.totalCost !== null && securityTotals.strict.totalCost !== null) {
		holdingTotals.strict.totalCost -= securityTotals.strict.totalCost;
	}
	holdingTotals.nonStrict.totalCost -= securityTotals.nonStrict.totalCost;

	if (securityTotals.strict.totalVotes && holdingTotals.strict.totalVotes) {
		holdingTotals.strict.totalVotes -= securityTotals.strict.totalVotes;
	}
	if (securityTotals.nonStrict.totalVotes && holdingTotals.nonStrict.totalVotes) {
		holdingTotals.nonStrict.totalVotes -= securityTotals.nonStrict.totalVotes;
	}
};

export type IssuersStack = (MipGroupHoldingResponse | IssuerHolding)[];

export const calculatePercentagesForHoldings = (issuersStack: IssuersStack): void => {
	const issuersHoldings: IssuerHolding[] = issuersStack.reduce(
		(acc: IssuerHolding[], cur: IssuersStack[0]) => {
			if ('issuerId' in cur) {
				acc.push(cur);
				return acc;
			} else {
				return acc.concat(cur.issuersHoldings);
			}
		},
		[]
	);
	issuersHoldings.forEach((issuerHolding) => {
		calculatePercentagesForHolders(issuerHolding.issuerSummarizedHolders, issuerHolding.totals);

		for (const securityType in issuerHolding.securityTypesHoldings) {
			const typeHolding = issuerHolding.securityTypesHoldings[securityType]!;
			calculatePercentagesForHolders(
				typeHolding.securityTypeSummarizedHolders,
				typeHolding.totals
			);

			for (const securityId in typeHolding.securitiesHoldings) {
				const securityHolding = typeHolding.securitiesHoldings[securityId]!;
				calculatePercentagesForHolders(
					securityHolding.securityHolders,
					securityHolding.totals
				);
			}
		}
	});
};

const calculatePercentagesForHolders = (
	holders: Dictionary<Holder>,
	holdingTotals: HoldingTotals
): void => {
	for (const holderId in holders) {
		const holder = holders[holderId]!;
		holder.nonStrict.amountPercent = getPercentage(
			holder.nonStrict.amount,
			holdingTotals.nonStrict.totalAmount
		);
		holder.strict.amountPercent = getPercentage(
			holder.strict.amount,
			holdingTotals.strict.totalAmount
		);

		if (holder.nonStrict.votes && holdingTotals.nonStrict.totalVotes) {
			holder.nonStrict.votesPercent = getPercentage(
				holder.nonStrict.votes,
				holdingTotals.nonStrict.totalVotes
			);
		}
		if (holder.strict.votes && holdingTotals.strict.totalVotes) {
			holder.strict.votesPercent = getPercentage(
				holder.strict.votes,
				holdingTotals.strict.totalVotes
			);
		}

		holder.nonStrict.totalCostPercent = getPercentage(
			holder.nonStrict.totalCost,
			holdingTotals.nonStrict.totalCost
		);
		holder.strict.totalCostPercent = getPercentage(
			holder.strict.totalCost,
			holdingTotals.strict.totalCost
		);
	}
};

export enum ManageCapTransactionsTableColumn {
	id = 'id',
	date = 'date',
	securityName = 'securityName',
	type = 'type',
	noType = 'noType',
	destinationPerson = 'destinationPerson',
	sourcePerson = 'sourcePerson',
	sourcePersonId = 'sourceId',
	destinationPersonId = 'destinationId',
	amount = 'units',
	price = 'cost',
	isTransactionValid = 'isTransactionValid',
	isPossibleRedemption = 'isPossibleRedemption',
	transactionError = 'transactionError',
	sourceBalance = 'sourceBalanceNonStrict',
	sourceBalanceNew = 'sourceBalanceNew',
	destBalance = 'destBalanceNonStrict',
	destBalanceNew = 'destBalanceNew',
	order = 'order',
}
